import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { Container, SpecialInput } from "./MultipleTagInputStyles";

// The default size of the whole input.
const MAX_NUMBER_OF_CHARACTERS = 40;
// Size in characters that occupies a tag including margins.
const APPROX_NUMBER_OF_CHARACTERS_PER_TAG = 9;
// Offset used for margins and the remove button.
const OFFSET = 5;

const filterValidTags = (tags) => {
  return tags.filter((tag) => {
    if (!tag) return false;
    const tagLength = tag.length;
    if (tagLength === 8) {
      const match = !!tag.match(/^(\d+)$/);
      return match;
    }
    if (tagLength === 14) {
      const match = !!tag.match(/^(\w+)$/);
      return match;
    }
    return false;
  });
};

function MultipleTagInput(props, ref) {
  const maxNumberOfCharacters =
    props.maxNumberOfCharacters || MAX_NUMBER_OF_CHARACTERS;

  const hasError = props.hasError;

  const [tags, updateTags] = useState(props.tagList || []);

  useEffect(() => {
    const filteredTags = filterValidTags(props.tagList);
    updateTags(filteredTags);
  }, [props.tagList]);

  // A way to communicate with parent component of which tags we currently
  // have.

  const updateTagValues = (tags) => {
    const filteredTags = filterValidTags(tags);

    updateTags(filteredTags);
    props.onTagListUpdate(filteredTags);
  };

  const [inputValue, updateInputValue] = useState("");
  const id = props.id ? props.id : "multiple-tag-input";

  const keyDownHanlder = (ev) => {
    const newTags = [...tags];
    let needsUpdate = false;
    if ([",", " "].includes(ev.key) && ev.target.value) {
      newTags.push(ev.target.value.toUpperCase());
      updateInputValue("");
      needsUpdate = true;
    }
    // Remove last tag.
    if (ev.key === "Backspace" && ev.target.value === "") {
      newTags.pop();
      needsUpdate = true;
    }

    if (needsUpdate && (!props.isValid || props.isValid(newTags.at(-1)))) {
      updateTagValues(newTags);
    }
  };

  const onChangeHandler = (ev) => {
    // Igonre spaces and commas.
    if ([inputValue + ",", inputValue + " "].includes(ev.target.value)) {
      return;
    }
    const newTags = ev.target.value
      .split(/[\s, ]+/)
      .filter((tag) => tag.length > 0);
    if (newTags.length === 0) {
      updateInputValue("");
    } else if (newTags.length === 1) {
      // User has not finished typing the tag.
      updateInputValue(newTags[0]);
    } else {
      // Several tags at the same time, this can happen
      // with copy pasting.
      updateInputValue("");
    }
  };

  const removeTagAt = (idx) => {
    const newTags = [...tags];
    newTags.splice(idx, 1);
    updateTagValues(newTags);
  };

  useImperativeHandle(ref, () => ({
    clear() {
      updateTagValues([]);
    },
  }));

  const inputSize = Math.max(
    APPROX_NUMBER_OF_CHARACTERS_PER_TAG,
    maxNumberOfCharacters - tags.join("").length - OFFSET * tags.length
  );

  // Includes click event
  const onBlur = (ev) => {
    const newTag = ev.target.value;
    if (!newTag) return;
    if (!props.isValid || (props.isValid && props.isValid(newTag))) {
      updateTagValues([...tags, newTag.toUpperCase()]);
    }
    updateInputValue("");
  };

  const tagsLength = tags.length < 3;
  return (
    <Container
      id={id}
      width={props.width || "100%"}
      hasError={hasError}
      height={props.height}
      TagInputWidth={props.TagInputWidth}
    >
      <ul
        className="flex-align-center"
        style={{
          flexWrap: `${tagsLength ? "inherit" : "wrap"}`,
          overflowY: "auto",
          height: `${tagsLength ? "auto" : "10vh"}`,
          paddingTop: `${tagsLength ? "0rem" : "1rem"}`,
          paddingBottom: `${tagsLength ? "0rem" : "1rem"}`,
        }}
      >
        {tags.map((tag, idx) => (
          <li
            key={tag + idx}
            className="tag font-size-li"
            style={{ marginTop: "0.3rem" }}
          >
            {tag}
            <button
              style={{ cursor: "pointer" }}
              type="button"
              onClick={() => removeTagAt(idx)}
            >
              x
            </button>
          </li>
        ))}
        <li>
          <SpecialInput
            height={props.height}
            onBlur={onBlur}
            width={tags.length < 3 ? "100%" : "15rem"}
            type="text"
            value={inputValue}
            onKeyDown={keyDownHanlder}
            onChange={onChangeHandler}
            size={inputSize}
            placeholder={
              props.placeholder && tags.length === 0 ? props.placeholder : ""
            }
          />
        </li>
      </ul>
    </Container>
  );
}

export default forwardRef(MultipleTagInput);
